'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  useLogin,
  useLoginWithEmail,
  useLoginWithOAuth,
  useLoginWithSms,
} from '@privy-io/react-auth';

import {
  useAuth,
  useBodyScrollLock,
  useT,
  getPersistedReferralCode,
} from '@/hooks';
import { useMixpanel } from '@/hooks/useMixpanel';
import { useAppDispatch } from '@/store';
import { closeModal } from '@/store/slices/ui.slice';
import { AuthModalErrorAlert } from './AuthModalErrorAlert';
import { PrivyOtpVerification } from './PrivyOtpVerification';

import { MESSAGES } from '@/constants/messages.constants';

// Google "G" logo SVG
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

// Apple logo SVG — always white so it works on the black icon box
const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255,255,255,0.92)">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.08-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

// Privy logo SVG (dot)
const PrivyDot = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <circle cx="4" cy="4" r="4" fill="#6B7280" />
  </svg>
);

const DIAL_CODES = [
  { label: 'MX', dial: '+52' },
  { label: 'US', dial: '+1' },
  { label: 'CA', dial: '+1' },
  { label: 'ES', dial: '+34' },
  { label: 'AR', dial: '+54' },
  { label: 'CO', dial: '+57' },
  { label: 'CL', dial: '+56' },
  { label: 'PE', dial: '+51' },
  { label: 'BR', dial: '+55' },
  { label: 'VE', dial: '+58' },
  { label: 'GT', dial: '+502' },
  { label: 'EC', dial: '+593' },
  { label: 'GB', dial: '+44' },
  { label: 'FR', dial: '+33' },
  { label: 'DE', dial: '+49' },
  { label: 'IT', dial: '+39' },
  { label: 'IN', dial: '+91' },
  { label: 'AU', dial: '+61' },
  { label: 'JP', dial: '+81' },
];

type AuthStep = 'main' | 'email_otp' | 'phone_otp';

const ICON_BOX_SX = {
  width: 28,
  height: 28,
  borderRadius: '8px',
  bgcolor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

const OPTION_BTN_SX = {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: 1.5,
  px: 1.5,
  py: 1.25,
  mb: 1,
  borderRadius: '12px',
  border: '1px solid',
  borderColor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
  bgcolor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  color: 'text.primary',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 500,
  fontSize: '0.9375rem',
  textAlign: 'left' as const,
  transition: 'background-color 0.15s',
  '&:hover': {
    bgcolor: (theme: any) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
} as const;

export interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const { t } = useT();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAuth();
  const {
    trackLoginSucceeded,
    trackLoginFailed,
    trackLoginStarted,
    trackSignupCompleted,
    trackReferralApplied,
    trackReferralScreenViewed,
    registerReferralCode,
  } = useMixpanel();
  useBodyScrollLock(isOpen);

  const [step, setStep] = useState<AuthStep>('main');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  // Which method is currently sending a code, so the spinner only shows on that
  // button. A single shared boolean lit up both the email and phone submit
  // buttons at once. `isSendingCode` stays as a derived "any send in progress"
  // flag for disabling inputs while a request is in flight.
  const [sendingMethod, setSendingMethod] = useState<'email' | 'sms' | null>(null);
  const isSendingCode = sendingMethod !== null;
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPhoneInline, setShowPhoneInline] = useState(false);
  const [dialCode, setDialCode] = useState(DIAL_CODES[0].dial);
  const [countryLabel, setCountryLabel] = useState(DIAL_CODES[0].label);
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<null | HTMLElement>(null);

  const handleClose = useCallback(() => {
    setStep('main');
    setEmail('');
    setPhone('');
    setOtpCode('');
    setError(null);
    setShowPhoneInline(false);
    setCountryMenuAnchor(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen && isAuthenticated && user) {
      handleClose();
    }
  }, [isOpen, isAuthenticated, user, handleClose]);

  // Referral funnel: when the auth prompt opens for a not-yet-authenticated user
  // who arrived via a referral link, record the "referral screen" step once.
  const referralScreenTrackedRef = useRef(false);
  useEffect(() => {
    if (!isOpen || isAuthenticated) {
      referralScreenTrackedRef.current = false;
      return;
    }
    if (referralScreenTrackedRef.current) return;
    const referralCode = getPersistedReferralCode();
    if (referralCode) {
      referralScreenTrackedRef.current = true;
      registerReferralCode(referralCode);
      trackReferralScreenViewed({ referral_code: referralCode });
    }
  }, [isOpen, isAuthenticated, registerReferralCode, trackReferralScreenViewed]);

  const onLoginComplete = useCallback(
    (params?: { isNewUser?: boolean; loginMethod?: string | null }) => {
      const loginMethod = params?.loginMethod || 'privy';
      const isNewUser = Boolean(params?.isNewUser);

      // Compute a real days_since_last_login from the last recorded login instead
      // of the previous hardcoded 0 (which made the "returning users" metric unusable).
      let daysSinceLastLogin: number | undefined;
      try {
        const last = localStorage.getItem('pm_last_login_at');
        if (last) {
          daysSinceLastLogin = Math.floor((Date.now() - Number(last)) / 86_400_000);
        }
        localStorage.setItem('pm_last_login_at', String(Date.now()));
      } catch {
        // localStorage unavailable — non-fatal, just omit the metric.
      }

      trackLoginSucceeded({
        login_method: loginMethod,
        days_since_last_login: daysSinceLastLogin,
        is_new_user: isNewUser,
      });

      // First-ever login == signup. Emit signup_completed (+ referral_applied if
      // the user arrived via a referral link) so the admin funnel and referral
      // analytics have real events instead of proxies.
      // NOTE: this only READS the persisted referral code for tracking — it must
      // NOT clear it. AuthInitializer is the single owner of the referral flow:
      // it sends the code to the backend login and clears it after the referral
      // is applied. Clearing here would race ahead of that call, drop the code,
      // and leave the user un-referred (which pops the InviteGate modal).
      if (isNewUser) {
        const referralCode = getPersistedReferralCode();
        if (referralCode) {
          registerReferralCode(referralCode);
          trackReferralApplied({ referral_code: referralCode, signup_method: loginMethod });
        }
        trackSignupCompleted({
          signup_method: loginMethod,
          referral_code: referralCode || undefined,
        });
      }

      // Embedded wallet creation is owned by AuthInitializer (always mounted via
      // providers.tsx): it reacts to `authenticated` flipping true and retries if
      // the wallet iframe isn't ready yet. Creating it here too raced that logic
      // and could throw "iframe not initialized" right after a headless login.
      dispatch(closeModal('signIn'));
    },
    [
      trackLoginSucceeded,
      trackSignupCompleted,
      trackReferralApplied,
      registerReferralCode,
      dispatch,
    ]
  );

  const onLoginError = useCallback(
    (err: any) => {
      const errStr = typeof err === 'string' ? err : err?.message || err?.type || '';
      if (!errStr.toLowerCase().includes('user_exited')) {
        trackLoginFailed({ login_method: 'privy', failure_reason: errStr });
        setError(errStr || t('messages.auth.loginFailed'));
      }
      setIsVerifying(false);
      setSendingMethod(null);
    },
    [trackLoginFailed, t]
  );

  const { sendCode: sendEmailCode, loginWithCode: loginWithEmailCode } = useLoginWithEmail({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  const { initOAuth } = useLoginWithOAuth({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  const { sendCode: sendSmsCode, loginWithCode: loginWithSmsCode } = useLoginWithSms({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  const { login: loginWithWallet } = useLogin({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  // ── Email flow ─────────────────────────────────────────────
  const handleEmailSubmit = async () => {
    if (!email.trim()) return;
    setSendingMethod('email');
    setError(null);
    try {
      trackLoginStarted({ signup_method: 'email', source_page: 'auth_modal' });
      await sendEmailCode({ email: email.trim() });
      setStep('email_otp');
    } catch (err: any) {
      const msg = err?.message || MESSAGES.AUTH.OTP_SEND_FAILED;
      toast.error(msg);
      setError(msg);
    } finally {
      setSendingMethod(null);
    }
  };

  const handleEmailOtpVerify = async () => {
    setIsVerifying(true);
    setError(null);
    try {
      await loginWithEmailCode({ code: otpCode });
    } catch {
      setIsVerifying(false);
    }
  };

  const handleEmailResend = async () => {
    setOtpCode('');
    setError(null);
    try {
      await sendEmailCode({ email: email.trim() });
    } catch (err: any) {
      setError(err?.message || MESSAGES.AUTH.OTP_SEND_FAILED);
    }
  };

  // ── Social flows ───────────────────────────────────────────
  const handleGoogleSignIn = () => {
    setError(null);
    trackLoginStarted({ signup_method: 'google', source_page: 'auth_modal' });
    initOAuth({ provider: 'google' });
  };

  const handleAppleSignIn = () => {
    setError(null);
    trackLoginStarted({ signup_method: 'apple', source_page: 'auth_modal' });
    initOAuth({ provider: 'apple' });
  };

  // ── WhatsApp / SMS flow ────────────────────────────────────
  const handleWhatsAppClick = () => {
    setError(null);
    setShowPhoneInline(true);
  };

  const handlePhoneSubmit = async () => {
    if (!phone.trim()) return;
    const fullNumber = dialCode + phone.replace(/\s/g, '');
    setSendingMethod('sms');
    setError(null);
    try {
      trackLoginStarted({ signup_method: 'sms', source_page: 'auth_modal' });
      await sendSmsCode({ phoneNumber: fullNumber });
      setOtpCode('');
      setStep('phone_otp');
    } catch (err: any) {
      const msg = err?.message || MESSAGES.AUTH.OTP_SEND_FAILED;
      toast.error(msg);
      setError(msg);
    } finally {
      setSendingMethod(null);
    }
  };

  const handlePhoneOtpVerify = async () => {
    setIsVerifying(true);
    setError(null);
    try {
      await loginWithSmsCode({ code: otpCode });
    } catch {
      setIsVerifying(false);
    }
  };

  const handlePhoneResend = async () => {
    setOtpCode('');
    setError(null);
    try {
      await sendSmsCode({ phoneNumber: phone.trim() });
    } catch (err: any) {
      setError(err?.message || MESSAGES.AUTH.OTP_SEND_FAILED);
    }
  };

  const handleBack = () => {
    setError(null);
    setOtpCode('');
    if (step === 'phone_otp') {
      setStep('main');
      setShowPhoneInline(true);
    } else {
      setShowPhoneInline(false);
      setStep('main');
    }
  };

  // ── Shared top bar (close / back) ─────────────────────────
  const renderTopBar = (showBack = false) => (
    <Box sx={{ position: 'relative', height: 52, flexShrink: 0 }}>
      {showBack && (
        <IconButton
          onClick={handleBack}
          aria-label={t('authModal.back')}
          sx={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
            color: 'text.secondary',
            width: 32,
            height: 32,
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
              color: 'text.primary',
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      <IconButton
        onClick={handleClose}
        aria-label="Close"
        sx={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
          color: 'text.secondary',
          width: 32,
          height: 32,
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.08)',
          '&:hover': {
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.08)',
            color: 'text.primary',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  // ── Main step ──────────────────────────────────────────────
  const renderMainStep = () => (
    <>
      {renderTopBar()}

      {/* App icon + title */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            width: { xs: 44, sm: 48 },
            height: { xs: 44, sm: 48 },
            borderRadius: '50%',
            overflow: 'hidden',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.primary',
            }}
          >
            <svg width="48" height="48" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="4" y1="16" x2="28" y2="16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </Box>
        </Box>
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            color: 'text.primary',
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            textAlign: 'center',
          }}
        >
          {t('authModal.loginOrSignUp')}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
        <AuthModalErrorAlert error={error} onClose={() => setError(null)} />

        {/* Google */}
        <Box component="button" type="button" onClick={handleGoogleSignIn} sx={OPTION_BTN_SX}>
          <Box
            sx={{
              ...ICON_BOX_SX,
              bgcolor: (theme: any) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.06)',
            }}
          >
            <GoogleIcon />
          </Box>
          {t('authModal.continueWithGoogle')}
        </Box>

        {/* Apple — always black box with white logo to match native Privy */}
        <Box
          component="button"
          type="button"
          onClick={handleAppleSignIn}
          sx={{ ...OPTION_BTN_SX, mb: 1 }}
        >
          <Box sx={{ ...ICON_BOX_SX, bgcolor: '#1d1d1f' }}>
            <AppleIcon />
          </Box>
          {t('authModal.continueWithApple')}
        </Box>

        {/* WhatsApp — expands into inline country + phone row when clicked */}
        {showPhoneInline ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 44,
                boxSizing: 'border-box',
                px: 1.5,
                mb: 1,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                gap: 1.25,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                },
                '&:focus-within': {
                  borderColor: 'primary.main',
                  boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              {/* Country code selector */}
              <Box
                component="button"
                type="button"
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  setCountryMenuAnchor(e.currentTarget)
                }
                disabled={isSendingCode}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'text.primary',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  p: 0,
                  height: '100%',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {countryLabel} {dialCode}
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Box>

              {/* Divider */}
              <Box
                sx={{
                  width: '1px',
                  height: 18,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                  flexShrink: 0,
                }}
              />

              {/* Phone number input */}
              <Box
                component="input"
                autoFocus
                type="tel"
                value={phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handlePhoneSubmit()}
                disabled={isSendingCode}
                placeholder="555 000 0000"
                sx={{
                  flex: 1,
                  height: '100%',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: 'text.primary',
                  fontFamily: 'inherit',
                  fontSize: '0.9375rem',
                  minWidth: 0,
                  p: 0,
                  '&::placeholder': { color: 'text.disabled', opacity: 1 },
                }}
              />

              {/* Submit */}
              <Box
                component="button"
                type="button"
                onClick={handlePhoneSubmit}
                disabled={isSendingCode || !phone.trim()}
                sx={{
                  background: 'none',
                  border: 'none',
                  cursor: phone.trim() && !isSendingCode ? 'pointer' : 'default',
                  color: phone.trim() && !isSendingCode ? 'primary.main' : 'text.disabled',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  p: 0,
                  height: '100%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {sendingMethod === 'sms' ? (
                  <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                ) : (
                  t('authModal.submit')
                )}
              </Box>
            </Box>

            {/* Country picker dropdown */}
            <Menu
              anchorEl={countryMenuAnchor}
              open={Boolean(countryMenuAnchor)}
              onClose={() => setCountryMenuAnchor(null)}
              slotProps={{ paper: { sx: { maxHeight: 240, minWidth: 140 } } }}
            >
              {DIAL_CODES.map((c) => (
                <MenuItem
                  key={`${c.label}-${c.dial}`}
                  selected={c.label === countryLabel && c.dial === dialCode}
                  onClick={() => {
                    setCountryLabel(c.label);
                    setDialCode(c.dial);
                    setCountryMenuAnchor(null);
                  }}
                  sx={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  {c.label}&nbsp;&nbsp;{c.dial}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Box component="button" type="button" onClick={handleWhatsAppClick} sx={OPTION_BTN_SX}>
            <Box sx={ICON_BOX_SX}>
              <WhatsAppIcon sx={{ fontSize: 20, color: 'inherit' }} />
            </Box>
            {t('authModal.continueWithWhatsApp')}
          </Box>
        )}

        {/* Email input */}
        <TextField
          fullWidth
          size="small"
          placeholder={t('authModal.emailPlaceholder')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
          disabled={isSendingCode}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box sx={ICON_BOX_SX}>
                  <EmailOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />
                </Box>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  component="button"
                  type="button"
                  onClick={handleEmailSubmit}
                  sx={{
                    background: 'none',
                    border: 'none',
                    cursor: email.trim() && !isSendingCode ? 'pointer' : 'default',
                    color: email.trim() && !isSendingCode ? 'primary.main' : 'text.disabled',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {sendingMethod === 'email' ? (
                    <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
                  ) : (
                    t('authModal.submit')
                  )}
                </Typography>
              </InputAdornment>
            ),
            sx: {
              height: 44,
              boxSizing: 'border-box',
              borderRadius: '12px',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              color: 'text.primary',
              gap: 1.25,
              px: 1.5,
              transition: 'all 0.2s ease-in-out',
              '& fieldset': {
                borderColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: '1px',
              },
              '& .MuiOutlinedInput-input': {
                px: 0,
                py: 0,
                height: '100%',
              },
              '& input::placeholder': { color: 'text.disabled', opacity: 1 },
              '& .MuiInputAdornment-positionStart': { ml: 0, mr: 0 },
              '& .MuiInputAdornment-positionEnd': { ml: 0, mr: 0 },
            },
          }}
        />

        {/* Wallet */}
        <Box
          component="button"
          type="button"
          onClick={() => loginWithWallet({ loginMethods: ['wallet'] } as any)}
          sx={OPTION_BTN_SX}
        >
          <Box sx={ICON_BOX_SX}>
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />
          </Box>
          {t('authModal.continueWithWallet')}
        </Box>
      </Box>

      {/* Protected by Privy footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          py: 2,
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
          {t('authModal.protectedBy')}
        </Typography>
        <PrivyDot />
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
          privy
        </Typography>
      </Box>
    </>
  );

  // ── Phone input step ───────────────────────────────────────
  const renderPhoneInputStep = () => (
    <>
      {renderTopBar(true)}
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 3 }}>
        <AuthModalErrorAlert error={error} onClose={() => setError(null)} />
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 1.5 }}>
          {t('authModal.phoneInputLabel')}
        </Typography>
        <TextField
          fullWidth
          autoFocus
          size="small"
          placeholder={t('authModal.phonePlaceholder')}
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handlePhoneSubmit()}
          disabled={isSendingCode}
          sx={{ mb: 2 }}
          InputProps={{
            sx: {
              height: 44,
              boxSizing: 'border-box',
              borderRadius: '12px',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              color: 'text.primary',
              px: 1.5,
              transition: 'all 0.2s ease-in-out',
              '& fieldset': {
                borderColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: '1px',
              },
              '& .MuiOutlinedInput-input': {
                px: 0,
                py: 0,
                height: '100%',
              },
              '& input::placeholder': { color: 'text.disabled', opacity: 1 },
            },
          }}
        />
        <Box
          component="button"
          type="button"
          onClick={handlePhoneSubmit}
          disabled={isSendingCode || !phone.trim()}
          sx={{
            width: '100%',
            py: 1.25,
            borderRadius: '12px',
            border: 'none',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 700,
            fontSize: '0.9375rem',
            fontFamily: 'inherit',
            cursor: isSendingCode || !phone.trim() ? 'not-allowed' : 'pointer',
            opacity: isSendingCode || !phone.trim() ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {sendingMethod === 'sms' ? (
            <>
              <CircularProgress size={16} sx={{ color: 'primary.contrastText' }} />{' '}
              {t('authModal.sending')}
            </>
          ) : (
            t('authModal.sendCode')
          )}
        </Box>
      </Box>
    </>
  );

  // ── OTP step (reused for email & phone) ───────────────────
  const renderOtpStep = (displayContact: string, onVerify: () => void, onResend: () => void) => (
    <>
      {renderTopBar(true)}
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 3 }}>
        <AuthModalErrorAlert error={error} onClose={() => setError(null)} />
        <PrivyOtpVerification
          email={displayContact}
          otpCode={otpCode}
          setOtpCode={setOtpCode}
          isAuthenticating={isVerifying}
          onVerify={onVerify}
          onResend={onResend}
        />
      </Box>
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="auth-modal-title"
      PaperProps={{
        elevation: 0,
        sx: (theme) => ({
          borderRadius: { xs: '16px', sm: '20px' },
          bgcolor: theme.palette.background.paper,
          background: theme.palette.background.paper,
          backgroundImage: 'none !important',
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          maxWidth: { xs: '100%', sm: '444px' },
          mx: 'auto',
          maxHeight: { xs: '88vh', sm: '90vh' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          boxShadow:
            theme.palette.mode === 'dark'
              ? `0 8px 40px ${alpha(theme.palette.common.black, 0.6)}, 0 0 40px ${alpha(theme.palette.primary.main, 0.06)}`
              : `0 8px 40px ${alpha(theme.palette.common.black, 0.15)}`,
        }),
      }}
      BackdropProps={{
        sx: (theme) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.65)'
              : 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }),
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {step === 'main' && renderMainStep()}
        {step === 'email_otp' && renderOtpStep(email, handleEmailOtpVerify, handleEmailResend)}
        {step === 'phone_otp' && renderOtpStep(phone, handlePhoneOtpVerify, handlePhoneResend)}
      </DialogContent>
    </Dialog>
  );
}

export default AuthModal;
